import React, { useState, useRef, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import { FiX, FiCamera, FiRefreshCw } from 'react-icons/fi';
import jsQR from 'jsqr';

const QRScanner = forwardRef(({ isOpen, onClose, onScan, onError,
  title = "Scan QR Code",
  instructions = "Position the QR code within the frame to scan"}, ref) => { 
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const animationFrameRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasPermission, setHasPermission] = useState(null);
  const [isScanning, setIsScanning] = useState(false);

  // Improved stopCamera function
  const stopCamera = useCallback(() => {
    console.log('Stopping camera...');
    
    if (isScanning) setIsScanning(false);

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        if (track.readyState === 'live') {
          track.stop();
          console.log('Track stopped:', track.kind);
        }
      });
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
      videoRef.current.pause();
    }

    setHasPermission(null);
    setError(null);
  }, [isScanning]);

  // Expose stopCamera through ref
  useImperativeHandle(ref, () => ({
    stopCamera
  }), [stopCamera]);

  // Memoized scan function to avoid recreating on every render
  const scanQRCode = useCallback(() => {
    if (!videoRef.current || !canvasRef.current || !isScanning) {
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d', { willReadFrequently: true });

    if (video.readyState === video.HAVE_ENOUGH_DATA) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      
      try {
        const code = jsQR(imageData.data, imageData.width, imageData.height, {
          inversionAttempts: "dontInvert",
        });
        
        if (code && code.data) {
          console.log(code)
          setIsScanning(false);
          if (onScan) {
            onScan(code.data);
          }
          return; // Stop scanning once found
        }
      } catch (qrError) {
        console.warn('QR detection error:', qrError);
      }
    }

    // Continue scanning
    animationFrameRef.current = requestAnimationFrame(scanQRCode);
  }, [onScan, isScanning]);

  // Initialize camera when modal opens
  useEffect(() => {
    console.log('isOpen changed:', isOpen);
    if (isOpen) {
      startCamera();
    } else {
      // Ensure camera is stopped when modal closes
      stopCamera();
    }

    // Cleanup function - this is crucial
    return () => {
      console.log('Component cleanup - stopping camera');
      stopCamera();
    };
  }, [isOpen, ]);

  // Start scanning when video is ready
  useEffect(() => {
    if (isScanning) {
      animationFrameRef.current = requestAnimationFrame(scanQRCode);
    }
    
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isScanning, scanQRCode]);

  const startCamera = async () => {
    setIsLoading(true);
    setError(null);
    setIsScanning(false);

    try {
      // Stop any existing stream first
      stopCamera();

      // Check if getUserMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Camera not supported in this browser');
      }

      const constraints = {
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280, min: 640, max: 1920 },
          height: { ideal: 720, min: 480, max: 1080 },
          aspectRatio: { ideal: 16/9 }
        },
        audio: false
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        
        // Wait for video to be ready
        const playVideo = () => {
          return new Promise((resolve, reject) => {
            videoRef.current.onloadedmetadata = () => {
              videoRef.current.play()
                .then(() => {
                  setHasPermission(true);
                  setIsLoading(false);
                  setIsScanning(true);
                  resolve();
                })
                .catch(reject);
            };
            
            videoRef.current.onerror = reject;
          });
        };

        await playVideo();
      }
    } catch (err) {
      console.error('Camera error:', err);
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
      setHasPermission(false);
      setIsLoading(false);
      
      // Call onError callback if provided
      if (onError) {
        onError(err, errorMessage);
      }
    }
  };

  const getErrorMessage = (error) => {
    if (error.message && error.message.includes('not supported')) {
      return 'Camera not supported in this browser.';
    }
    
    switch (error.name) {
      case 'NotAllowedError':
        return 'Camera permission denied. Please allow camera access and try again.';
      case 'NotFoundError':
        return 'No camera found on this device.';
      case 'NotSupportedError':
        return 'Camera not supported on this device.';
      case 'NotReadableError':
        return 'Camera is being used by another application.';
      case 'OverconstrainedError':
        return 'Camera constraints not supported. Trying with basic settings...';
      default:
        return 'Failed to access camera. Please try again.';
    }
  };

  // Updated handleClose function
  const handleClose = () => {
    // Stop camera first, then notify parent
    stopCamera();
    onClose();
  };

  const handleRetry = async () => {
    if (error && error.includes('constraints')) {
      // Try with basic constraints
      setIsLoading(true);
      setError(null);
      
      try {
        const basicConstraints = {
          video: true,
          audio: false
        };
        
        const stream = await navigator.mediaDevices.getUserMedia(basicConstraints);
        streamRef.current = stream;
        
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => {
            videoRef.current.play().then(() => {
              setHasPermission(true);
              setIsLoading(false);
              setIsScanning(true);
            });
          };
        }
      } catch (retryError) {
        setError(getErrorMessage(retryError));
        setIsLoading(false);
      }
    } else {
      startCamera();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-900 bg-opacity-75"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">
              {title}
            </h3>
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>

          {/* Scanner Area */}
          <div className="relative">
            <div className="w-full aspect-square bg-black rounded-xl overflow-hidden shadow-lg relative">
              {/* Video Element */}
              <video
                ref={videoRef}
                className="w-full h-full object-cover"
                playsInline
                muted
                autoPlay
                style={{ 
                  display: hasPermission && !error ? 'block' : 'none',
                  transform: 'scaleX(-1)' // Mirror the video for better UX
                }}
              />
              
              {/* Hidden canvas for image processing */}
              <canvas
                ref={canvasRef}
                className="hidden"
              />

              {/* Loading State */}
              {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
                  <div className="text-center text-white">
                    <FiRefreshCw className="w-8 h-8 mx-auto mb-2 animate-spin" />
                    <p className="text-sm">Starting camera...</p>
                    <p className="text-xs text-gray-400 mt-1">Please allow camera access</p>
                  </div>
                </div>
              )}

              {/* Error State */}
              {error && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-900 text-white p-4">
                  <div className="text-center">
                    <FiCamera className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                    <p className="text-sm mb-4">{error}</p>
                    <button
                      onClick={handleRetry}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                    >
                      Try Again
                    </button>
                  </div>
                </div>
              )}

              {/* Scanner Overlay */}
              {hasPermission && !error && !isLoading && (
                <div className="absolute inset-0 pointer-events-none">
                  <div className="w-full h-full relative">
                    {/* Corner guides */}
                    <div className="absolute top-6 left-6 w-8 h-8 border-l-4 border-t-4 border-white rounded-tl-lg opacity-80" />
                    <div className="absolute top-6 right-6 w-8 h-8 border-r-4 border-t-4 border-white rounded-tr-lg opacity-80" />
                    <div className="absolute bottom-6 left-6 w-8 h-8 border-l-4 border-b-4 border-white rounded-bl-lg opacity-80" />
                    <div className="absolute bottom-6 right-6 w-8 h-8 border-r-4 border-b-4 border-white rounded-br-lg opacity-80" />
                    
                    {/* Scanning animation */}
                    {isScanning && (
                      <div className="absolute inset-6 border-2 border-white border-opacity-30 rounded-lg">
                        <div className="absolute inset-x-0 top-1/2 h-0.5 bg-white opacity-75 animate-pulse transform -translate-y-1/2" />
                      </div>
                    )}
                    
                    {/* Status indicator */}
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-xs">
                      {isScanning ? 'Scanning...' : 'Ready'}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Instructions */}
            <div className="mt-4 text-center">
              <p className="text-sm text-gray-600">
                {instructions}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default QRScanner;